import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  UncontrolledDropdown,
} from 'reactstrap';
import { useTranslation } from '../../translations';
import { CashierContextProps } from './cashiercontext';
import { ConfigureNFC } from './nfc/configure';
import { ReceiptPrinterWeb } from './printers/ReceiptPrinter';
import { Scanner, useScannerContext } from './scanner';
import { ConfigureZebraPrinter } from './zebra/configure';

interface PrinterModalProps {
  readonly close: () => void;
  readonly settings: CashierContextProps;
}

export const PrinterModal: FC<PrinterModalProps> = ({ close, settings }) => {
  const { ts } = useTranslation();
  const { addScanner, scanners } = useScannerContext();
  const [activeReceiptPrinters, setActiveReceiptPrinters] = useState<ReceiptPrinterWeb[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isWebUSBAvailable = navigator.usb !== undefined;

  const refreshReceiptPrinters = useCallback(() => {
    const data = settings.receiptPrinterManagers.flatMap((m) => m.getPrinters());
    setActiveReceiptPrinters(data);
  }, [setActiveReceiptPrinters, settings]);

  useEffect(() => {
    for (const m of settings.receiptPrinterManagers) {
      m.addEventListener('connectedDevice', refreshReceiptPrinters);
      m.addEventListener('disconnectedDevice', refreshReceiptPrinters);
    }

    return () => {
      for (const m of settings.receiptPrinterManagers) {
        m.removeEventListener('connectedDevice', refreshReceiptPrinters);
        m.removeEventListener('disconnectedDevice', refreshReceiptPrinters);
      }
    };
  }, [settings.receiptPrinterManagers, refreshReceiptPrinters]);

  useEffect(() => {
    refreshReceiptPrinters();
  }, [refreshReceiptPrinters]);

  const onClickAddScanner = useCallback(async () => {
    const d = await navigator.serial?.requestPort();
    if (d) {
      addScanner(d);
    }
  }, [addScanner]);

  return (
    <Modal
      className="modal-large print-settings-modal hide-print"
      fade={false}
      isOpen
      toggle={close}
    >
      <ModalHeader toggle={close}>{ts('configure_devices')}</ModalHeader>
      <ModalBody>
        <ConfigureZebraPrinter />
        <ScannerSection
          isWebUSBAvailable={isWebUSBAvailable}
          onClickAddScanner={onClickAddScanner}
          scanners={scanners}
        />
        <ReceiptPrinterSection
          activeReceiptPrinters={activeReceiptPrinters}
          isWebUSBAvailable={isWebUSBAvailable}
          refreshReceiptPrinters={refreshReceiptPrinters}
          settings={settings}
        />
        <ConfigureNFC />
      </ModalBody>
    </Modal>
  );
};

interface ScannerSectionProps {
  readonly isWebUSBAvailable: boolean;
  readonly onClickAddScanner: () => void;
  readonly scanners: Scanner[];
}

const ScannerSection: FC<ScannerSectionProps> = ({
  isWebUSBAvailable,
  scanners,
  onClickAddScanner,
}) => {
  const { ts } = useTranslation();
  return (
    <>
      <h4>
        <div>{ts('scanners')}</div>
        {isWebUSBAvailable && (
          <UncontrolledDropdown>
            <DropdownToggle caret>{ts('add_manually')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem key="serialscanner" onClick={onClickAddScanner}>
                {ts('scanner_with_web_serial')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
      </h4>
      <div>
        {scanners.length === 0 && (
          <div className="print-settings-emptystate">
            <p>
              {ts(
                isWebUSBAvailable
                  ? 'no_serial_scanners_connected_try'
                  : 'it_seems_like_your_browser',
              )}
              <br />
              <small>
                <strong>{ts('usb_keyboard_scanners_are_always')}</strong>
              </small>
            </p>
          </div>
        )}
        {scanners.map((scanner) => {
          return <ScannerItem key={scanner.info.usbProductId} scanner={scanner} />;
        })}
      </div>
    </>
  );
};

const ScannerItem: FC<{ readonly scanner: Scanner }> = ({ scanner }) => {
  const { ts } = useTranslation();
  const { removeScanner } = useScannerContext();
  const onDelete = useCallback(() => {
    removeScanner(scanner.info);
  }, [scanner, removeScanner]);

  return (
    <div className="print-settings-printer">
      <div className="print-settings-printer-name">
        <div>
          <strong>{scanner.info.usbProductId}</strong>
        </div>
        <div>
          {scanner.info.usbVendorId}
          {ts(scanner.port ? 'connected' : 'disconnected')}
        </div>
      </div>
      <div>
        <Button color="secondary" onClick={onDelete} outline>
          {ts('remove')}
        </Button>
      </div>
    </div>
  );
};

interface ReceiptPrinterSectionProps {
  readonly activeReceiptPrinters: ReceiptPrinterWeb[];
  readonly settings: CashierContextProps;
  readonly refreshReceiptPrinters: () => void;
  readonly isWebUSBAvailable: boolean;
}

const ReceiptPrinterSection: FC<ReceiptPrinterSectionProps> = ({
  activeReceiptPrinters,
  settings,
  refreshReceiptPrinters,
  isWebUSBAvailable,
}) => {
  const { ts } = useTranslation();
  return (
    <>
      <h4>
        <div>{ts('receipt_printers')}</div>
        {settings.receiptPrinterManagers.length > 0 && isWebUSBAvailable && (
          <UncontrolledDropdown>
            <DropdownToggle caret>{ts('add_manually')}</DropdownToggle>
            <DropdownMenu>
              {settings.receiptPrinterManagers.map((m) => (
                <DropdownItem
                  key={m.type}
                  onClick={async () => {
                    await m.startManualConnect!();
                    refreshReceiptPrinters();
                  }}
                >
                  {m.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
      </h4>
      <div>
        {activeReceiptPrinters.length === 0 && (
          <div className="print-settings-emptystate">
            {ts(
              isWebUSBAvailable
                ? 'no_receipt_printers_connected_try'
                : 'it_seems_like_your_browser',
            )}
          </div>
        )}
        {activeReceiptPrinters.map((printer) => (
          <ReceiptPrinterItem key={printer.serial} printer={printer} />
        ))}
      </div>
    </>
  );
};

interface ReceiptPrinterItemProps {
  readonly printer?: ReceiptPrinterWeb;
}

const ReceiptPrinterItem: FC<ReceiptPrinterItemProps> = ({ printer }) => {
  const { ts } = useTranslation();
  if (printer === undefined) {
    return null;
  }

  return (
    <div className="print-settings-printer">
      <div className="print-settings-printer-name">
        <div>
          <strong>{printer.name}</strong>
        </div>
      </div>
      <div>
        <Button
          color="secondary"
          onClick={async () => {
            await printer.testPrint();
          }}
        >
          {ts('test_print')}
        </Button>
        <Button
          color="secondary"
          onClick={async () => {
            await printer.drawerKick();
          }}
        >
          {ts('kick_cash_drawer')}
        </Button>
      </div>
    </div>
  );
};
